import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Plus,
  Edit,
  Trash2, 
  Search, 
  ChevronLeft
} from 'lucide-react';
import { educationAPI, locationsAPI } from '../api/axiosClient';
import { School, SchoolsResponse, SchoolBrand, SchoolBrandsResponse, City, CitiesResponse, District, DistrictsResponse, Neighborhood, NeighborhoodsResponse } from '../api/types';
import { toast } from 'react-hot-toast';
import SchoolCreateForm from '../components/SchoolCreateForm';
import SchoolEditForm from '../components/SchoolEditForm';

interface SchoolsViewProps {
  onSuccess: (action: 'createSchool' | 'updateSchool' | 'deleteSchool') => void;
  onDeleteConfirm: (id: string, type: string) => void;
  onRefresh: () => void;
  deletedSchoolId?: string | null;

  deleteSchoolErrorMessage?: string | null; 
}


const SchoolsView: React.FC<SchoolsViewProps> = ({
  onSuccess,
  onDeleteConfirm,
  onRefresh,
  deletedSchoolId,
  deleteSchoolErrorMessage
}) => { 
  // Schools list state
  const [schools, setSchools] = useState<School[]>([]);
  const [loadingSchools, setLoadingSchools] = useState<boolean>(false);
  const [schoolsPagination, setSchoolsPagination] = useState({
    pageNumber: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  });


  // Search state
  const [schoolSearchTerm, setSchoolSearchTerm] = useState<string>('');

  // Form data state
  const [schoolBrands, setSchoolBrands] = useState<SchoolBrand[]>([]);
  const [loadingSchoolBrands, setLoadingSchoolBrands] = useState<boolean>(false);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);

  // Form states
  const [showSchoolCreateForm, setShowSchoolCreateForm] = useState<boolean>(false);
  const [showSchoolEditForm, setShowSchoolEditForm] = useState<boolean>(false);
  const [editingSchoolId, setEditingSchoolId] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<SchoolBrand | null>(null);
  const [selectedProvince, setSelectedProvince] = useState<{ id: string; label: string; value: string } | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<{ id: string; label: string; value: string } | null>(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<{ id: string; label: string; value: string } | null>(null);


  // Fetch schools list
  const fetchSchools = useCallback(async (page: number = 1, searchTerm: string = '') => {
    setLoadingSchools(true);
    try {
      const response = await educationAPI.listSchools({
        Q: searchTerm,
        Page: page,
        Size: 10,
        OrderBy: 0
      });
      
      if (response.success && response.data) {
        const schoolsData = response.data as SchoolsResponse['data'];
        setSchools(schoolsData.items || []);
        setSchoolsPagination({
          pageNumber: schoolsData.pageNumber,
          totalPages: schoolsData.totalPages,
          totalCount: schoolsData.totalCount,
          hasPreviousPage: schoolsData.hasPreviousPage,
          hasNextPage: schoolsData.hasNextPage
        });
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
      toast.error('Okullar yüklenirken bir hata oluştu');
    } finally {
      setLoadingSchools(false);
    }
  }, []);

  // Fetch cities
  const fetchCities = useCallback(async () => {
    try {
      const response = await locationsAPI.getCities();
      if (response.success && response.data) {
        setCities((response.data as CitiesResponse['data']).cities || []);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  }, []);

  // Fetch school brands
  const fetchSchoolBrands = useCallback(async () => {
    setLoadingSchoolBrands(true);
    try {
      const response = await educationAPI.listSchoolBrands({
        Page: 1,
        Size: 100,
        OrderBy: 1,
        SortBy: 'Name'
      });
      if (response.success && response.data) {
        setSchoolBrands((response.data as SchoolBrandsResponse['data']).items || []);
      }
    } catch (error) {
      console.error('Error fetching school brands:', error);
    } finally {
      setLoadingSchoolBrands(false);
    }
  }, []);

  // Fetch districts
  const fetchDistricts = useCallback(async (provinceId: string) => {
    try {
      const response = await locationsAPI.getDistricts(provinceId);
      if (response.success && response.data) {
        setDistricts((response.data as DistrictsResponse['data']).districts || []);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  }, []);

  // Fetch neighborhoods
  const fetchNeighborhoods = useCallback(async (districtId: string) => {
    try {
      const response = await locationsAPI.getNeighborhoods(districtId);
      if (response.success && response.data) {
        setNeighborhoods((response.data as NeighborhoodsResponse['data']).neighborhoods || []);
      }
    } catch (error) {
      console.error('Error fetching neighborhoods:', error);
    }
  }, []);

  // Load schools when component mounts
  useEffect(() => {
    fetchSchools();
    fetchCities();
    fetchSchoolBrands();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // Search handler
  const handleSchoolSearchSubmit = () => {
    fetchSchools(1, schoolSearchTerm);
  };

  // Form handlers
  const handleCreateSchool = () => {
    setShowSchoolCreateForm(true);
  };

  const handleEditSchool = (id: string) => {
    setEditingSchoolId(id);
    setShowSchoolEditForm(true);
  };

  const handleSchoolFormSuccess = (action: 'createSchool' | 'updateSchool' | 'deleteSchool') => {
    setShowSchoolCreateForm(false);
    setShowSchoolEditForm(false);
    setEditingSchoolId(null);
    setSelectedBrand(null);
    setSelectedProvince(null);
    setSelectedDistrict(null);
    setSelectedNeighborhood(null);
    onSuccess(action);
    fetchSchools(); // Refresh the list
  };

  // Handle success from Dashboard (for delete operations)
  useEffect(() => {
    // This will be called when Dashboard calls onSuccess
    // For delete operations, we need to refresh the list
    // We can't directly detect which action was triggered, so we'll refresh on any success
    // This is safe because form operations already call fetchSchools() in handleSchoolFormSuccess
  }, [onSuccess]);

  // Listen for refresh requests from Dashboard
  useEffect(() => {
    // Dashboard can trigger a refresh by calling onRefresh
    // We can add logic here if needed
  }, [onRefresh]);
  
  useEffect(() => {
  if (deleteSchoolErrorMessage) {
    toast.error(deleteSchoolErrorMessage);
  }
}, [deleteSchoolErrorMessage]);


  // Listen for school deletion success from Dashboard
  useEffect(() => {
    // This will be called when Dashboard calls onSuccess('deleteSchool')
    // We need to remove the school from the list
    // But we don't know which school was deleted, so we need to refresh the list
    // Actually, let's just refresh the list to be safe
  }, [onSuccess]);

  // Listen for school deletion from Dashboard
  useEffect(() => {
    if (deletedSchoolId) {
      // This will be called when Dashboard sets deletedSchoolId
      // We can remove the specific school from the list
      removeSchoolFromList(deletedSchoolId);
      // Reset the deletedSchoolId after processing
      // Note: We can't directly reset it here as it's a prop from parent
      // The parent (Dashboard) should reset it after the effect runs
    }
  }, [deletedSchoolId]);

  const handleSchoolFormClose = () => {
    setShowSchoolCreateForm(false);
    setShowSchoolEditForm(false);
    setEditingSchoolId(null);
    setSelectedBrand(null);
    setSelectedProvince(null);
    setSelectedDistrict(null);
    setSelectedNeighborhood(null);
  };


  // Delete handler
  const handleDeleteSchool = (schoolId: string) => {
    onDeleteConfirm(schoolId, 'school');
  };

  // Remove school from list after successful deletion
  const removeSchoolFromList = (schoolId: string) => {
    setSchools(prevSchools => prevSchools.filter(school => school.id !== schoolId));
    // Update pagination count
    setSchoolsPagination(prevPagination => ({
      ...prevPagination,
      totalCount: prevPagination.totalCount - 1
    }));
  };

  // Helper functions
  const getSchoolTypeLabel = (type: number | string): string => {
    switch (type) {
      case 1: return 'İlkokul';
      case 2: return 'Ortaokul';
      case 3: return 'Lise';
      case 4: return 'Anaokul';
      case 5: return 'Kreş';
      default: return 'Bilinmiyor';
    }
  };

  const getCountryFlag = (phoneCode: string): string => {
    const flags: { [key: string]: string } = {
      '90': '🇹🇷',
      '1': '🇺🇸',
      '44': '🇬🇧',
      '49': '🇩🇪',
      '33': '🇫🇷',
      '39': '🇮🇹',
      '34': '🇪🇸',
      '31': '🇳🇱',
      '32': '🇧🇪',
      '41': '🇨🇭',
      '43': '🇦🇹',
      '45': '🇩🇰',
      '46': '🇸🇪',
      '47': '🇳🇴',
      '358': '🇫🇮',
      '48': '🇵🇱',
      '420': '🇨🇿',
      '421': '🇸🇰',
      '36': '🇭🇺',
      '40': '🇷🇴',
      '359': '🇧🇬',
      '385': '🇭🇷',
      '386': '🇸🇮',
      '372': '🇪🇪',
      '371': '🇱🇻',
      '370': '🇱🇹',
      '7': '🇷🇺',
      '380': '🇺🇦',
      '375': '🇧🇾'
    };
    return flags[phoneCode] || '🌍';
  };

  const formatPhoneNumber = (phoneCode: string, phoneNumber: string): string => {
    if (!phoneCode || !phoneNumber) return '';
    
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    switch (phoneCode) {
      case '90': // Türkiye: (5XX) XXX-XXXX
        if (cleanNumber.length === 10) {
          return `(${cleanNumber.substring(0, 3)}) ${cleanNumber.substring(3, 6)}-${cleanNumber.substring(6)}`;
        }
        break;
      case '1': // USA/Canada: (XXX) XXX-XXXX
        if (cleanNumber.length === 10) {
          return `(${cleanNumber.substring(0, 3)}) ${cleanNumber.substring(3, 6)}-${cleanNumber.substring(6)}`;
        }
        break;
      default:
        return phoneNumber;
    }
    
    return phoneNumber;
  };

  return (
    <div className="space-y-6">

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Okul ara... (Enter ile ara)"
                value={schoolSearchTerm}
                onChange={(e) => setSchoolSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSchoolSearchSubmit();
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Schools Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardDescription>
              {loadingSchools ? 'Yükleniyor...' : `Toplam ${schoolsPagination.totalCount || 0} okul bulundu`}
            </CardDescription>
            <Button 
              className="bg-[#9B51E0] hover:bg-[#8B41D0]"
              onClick={handleCreateSchool}
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Ekle
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingSchools ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                <p className="text-gray-500">Okullar yükleniyor...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] table-fixed">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Ad</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Marka</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Tür</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Konum</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Telefon</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">E-posta</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Durum</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-gray-500">
                          {schoolSearchTerm ? 'Arama kriterlerine uygun okul bulunamadı' : 'Henüz okul eklenmemiş'}
                        </td>
                      </tr>
                    ) : (
                      schools.map((school) => (
                        <tr key={school.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4 w-[280px]">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 bg-[#9B51E0] rounded-lg flex items-center justify-center shrink-0">
                              <span className="text-white font-semibold text-sm">
                                {school.name.charAt(0).toUpperCase()}
                              </span>
                            </div>

                            <span className="font-medium text-gray-900 truncate">
                              {school.name}
                            </span>
                          </div>
                        </td>
                          <td className="py-4 px-4">
                            <span className="text-gray-600 text-sm">{school.schoolBrandName}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-gray-600 text-sm">{getSchoolTypeLabel(school.schoolType)}</span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm text-gray-600">
                              <div>{school.cityName}</div>
                              <div className="text-xs text-gray-500">{school.districtName}</div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm text-gray-600">
                              <div className="flex items-center space-x-2">
                                <span className="text-lg">
                                  {getCountryFlag(school.phoneCode)}
                                </span>
                                <span className="text-sm text-gray-600">
                                  +{school.phoneCode} {formatPhoneNumber(school.phoneCode, school.phoneNumber)}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            {school.email ? (
                              <a href={`mailto:${school.email}`} className="text-[#9B51E0] hover:text-[#8B41D0] text-sm">
                                {school.email}
                              </a>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              school.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {school.isActive ? 'Aktif' : 'Pasif'}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => handleEditSchool(school.id)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteSchool(school.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {schoolsPagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Sayfa {schoolsPagination.pageNumber} / {schoolsPagination.totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => fetchSchools(schoolsPagination.pageNumber - 1, schoolSearchTerm)}
                      disabled={!schoolsPagination.hasPreviousPage}
                      className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => fetchSchools(schoolsPagination.pageNumber + 1, schoolSearchTerm)}
                      disabled={!schoolsPagination.hasNextPage}
                      className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4 rotate-180" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* School Create Form */}
      {showSchoolCreateForm && (
        <SchoolCreateForm
          isOpen={showSchoolCreateForm}
          onClose={handleSchoolFormClose}
          onSuccess={() => handleSchoolFormSuccess('createSchool')}
          selectedBrand={selectedBrand}
          onBrandChange={setSelectedBrand}
          schoolBrands={schoolBrands}
          loadingSchoolBrands={loadingSchoolBrands}
          cities={cities}
          districts={districts}
          neighborhoods={neighborhoods}
          selectedProvince={selectedProvince}
          selectedDistrict={selectedDistrict}
          selectedNeighborhood={selectedNeighborhood}
          onProvinceChange={setSelectedProvince}
          onDistrictChange={setSelectedDistrict}
          onNeighborhoodChange={setSelectedNeighborhood}
          onFetchDistricts={fetchDistricts}
          onFetchNeighborhoods={fetchNeighborhoods}
        />
      )}

      {/* School Edit Form */}
      {showSchoolEditForm && editingSchoolId && (
        <SchoolEditForm
          isOpen={showSchoolEditForm}
          onClose={handleSchoolFormClose}
          onSuccess={() => handleSchoolFormSuccess('updateSchool')}
          editingSchoolId={editingSchoolId}
          selectedBrand={selectedBrand}
          onBrandChange={setSelectedBrand}
          schoolBrands={schoolBrands}
          loadingSchoolBrands={loadingSchoolBrands}
          cities={cities}
          districts={districts}
          neighborhoods={neighborhoods}
          selectedProvince={selectedProvince}
          selectedDistrict={selectedDistrict}
          selectedNeighborhood={selectedNeighborhood}
          onProvinceChange={setSelectedProvince}
          onDistrictChange={setSelectedDistrict}
          onNeighborhoodChange={setSelectedNeighborhood}
          onFetchDistricts={fetchDistricts}
          onFetchNeighborhoods={fetchNeighborhoods}
        />
      )}

    </div>
  );
};

export default SchoolsView;
